// Admin → Integrations → Supabase projects. Supports multiple slots
// (one row per connected external Supabase project, keyed by slug).
//
// UX:
//   - Top: "Add another project" button → expands a form.
//   - Body: one card per existing instance with config + enable toggle +
//           "Browse data" (routes to /data/external/:slug) + "Remove".
//   - Card header shows display_name, project_ref, and last updated.
//
// All writes go through saveIntegration()/deleteIntegration() from
// sdk-bridge, which signs via the Symbols server proxy and lets the
// edge function vault-write + upsert the row.

import {
  loadIntegrationCatalogue,
  saveIntegration,
  deleteIntegration
} from '../../integrations/index.js'
// resolveScope is a shared-library function; invoke via el.call('resolveScope', draft, defaultScopeType).

const KIND = 'supabase_project'
const DEFAULT_SCOPE = 'project'  // supabase is per-project by nature

const _loadInstances = async (root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) {
    if (!root._supabaseHubWatcher) {
      root._supabaseHubWatcher = setInterval(() => {
        if (root?.jwtClaims?.workspace_org_id) {
          clearInterval(root._supabaseHubWatcher)
          root._supabaseHubWatcher = null
          _loadInstances(root)
        }
      }, 300)
    }
    return
  }
  const data = await loadIntegrationCatalogue(orgId)
  root.update({ _integrationHub: data })
}

const _slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '')
    .slice(0, 40) || `slot-${Math.random().toString(36).slice(2, 6)}`

export const adminIntegrationsSupabase = {
  extends: 'AppPage',
  metadata: { title: 'Supabase / Integrations / Symbols Workspace' },
  onInit: (el, s) => {
    if (!el.call('shouldRedirectNonAdmin', s)) return
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/', root, {}, { pushState: true })
  },
  onCreate: (el, s) => _loadInstances(s.root),

  AdminSidebar: {},

  AdminMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: {
        BackLink: {
          tag: 'button',
          type: 'button',
          padding: '0',
          background: 'transparent',
          border: 'none',
          color: 'caption',
          fontSize: 'Z2',
          cursor: 'pointer',
          text: '← Integrations',
          onClick: (e, el, s) => {
            const root = el.call('findRouteRoot', el)
            if (root) el.router('/admin/integrations', root)
          }
        },
        Title: { text: 'Supabase projects' }
      },
      RightGroup: {
        AddBtn: {
          tag: 'button',
          type: 'button',
          padding: 'Z A',
          round: 'Y',
          background: 'blue',
          color: 'white',
          fontSize: 'Z2',
          fontWeight: '700',
          cursor: 'pointer',
          border: 'none',
          text: '+ Connect another project',
          onClick: (e, el, s) => {
            s.root.update({
              _newSupabaseDraft: {
                slug: '',
                displayName: '',
                url: '',
                projectRef: '',
                anonKey: '',
                serviceKey: ''
              },
              _supabaseFormOpen: true
            })
          }
        }
      }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'A',

      Hint: {
        tag: 'p',
        fontSize: 'Z2',
        color: 'caption',
        margin: '0',
        text: 'Every connected Supabase project becomes browsable in /data/external and editable in the editor CMS. Service-role keys live in Vault; they are never sent back to the browser after save.'
      },

      NewForm: { extends: 'SupabaseNewForm' },
      Instances: { extends: 'SupabaseInstanceList' }
    }
  }
}

export const SupabaseNewForm = {
  hide: (el, s) => !s.root?._supabaseFormOpen,
  flow: 'y',
  gap: 'Z1',
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'blue',
  padding: 'A',

  Title: {
    tag: 'h3',
    fontSize: 'A',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    text: 'Connect a new Supabase project'
  },

  Fields: {
    flow: 'y',
    gap: 'Z1',
    childExtends: 'SupabaseFormField',
    childrenAs: 'state',
    children: () => [
      { label: 'Display name', placeholder: 'aihouse / hotcard / benefits', draftKey: 'displayName' },
      { label: 'Slug (URL id — lowercase, hyphens)', placeholder: 'aihouse', draftKey: 'slug' },
      { label: 'Project URL', placeholder: 'https://xfzmadxocvkkxrdbonrx.supabase.co', draftKey: 'url' },
      { label: 'Project ref (ID from Supabase dashboard)', placeholder: 'xfzmadxocvkkxrdbonrx', draftKey: 'projectRef' },
      { label: 'Anon key (optional — enables realtime subscriptions)', placeholder: 'eyJhbGciOiJI...', draftKey: 'anonKey' }
    ]
  },

  ScopePicker: {
    extends: 'IntegrationScopePicker',
    draftKey: '_newSupabaseDraft',
    defaultScopeType: DEFAULT_SCOPE
  },

  ServiceKey: {
    flow: 'y',
    gap: 'V',
    Label: {
      tag: 'label',
      fontSize: 'Y2',
      color: 'caption',
      fontWeight: '700',
      text: 'Service role key (required — written to Vault)'
    },
    Textarea: {
      tag: 'textarea',
      rows: '3',
      width: '100%',
      padding: 'Z',
      round: 'Y',
      background: 'document',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontFamily: 'mono',
      fontSize: 'Z2',
      placeholder: 'eyJhbGciOi... (service_role key from Supabase dashboard → Settings → API)',
      onInput: (e, el, s) => {
        s.root.update({
          _newSupabaseDraft: {
            ...(s.root?._newSupabaseDraft || {}),
            serviceKey: e.target.value
          }
        })
      }
    },
    Hint: {
      tag: 'p',
      fontSize: 'Y2',
      color: 'caption',
      margin: '0',
      text: 'Required. Bypasses RLS on the target project for admin reads/writes.'
    }
  },

  Actions: {
    flow: 'x',
    gap: 'Z',
    align: 'center flex-end',
    marginTop: 'Z',

    CancelBtn: {
      tag: 'button',
      type: 'button',
      padding: 'V Z',
      round: 'Y',
      background: 'transparent',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      cursor: 'pointer',
      fontSize: 'Y2',
      text: 'Cancel',
      onClick: (e, el, s) => {
        s.root.update({ _supabaseFormOpen: false, _newSupabaseDraft: null })
      }
    },

    SaveBtn: {
      tag: 'button',
      type: 'button',
      padding: 'V A',
      round: 'Y',
      background: 'blue',
      color: 'white',
      fontSize: 'Y2',
      fontWeight: '700',
      cursor: 'pointer',
      border: 'none',
      text: (el, s) => (s.root?._supabaseSaving ? 'Saving…' : 'Connect project'),
      onClick: async (e, el, s) => {
        const orgId = s.root?.jwtClaims?.workspace_org_id
        const draft = s.root?._newSupabaseDraft || {}
        if (!orgId) {
          el.call('openNotification', { title: 'No active workspace', type: 'error' })
          return
        }
        if (!draft.displayName || !draft.url || !draft.projectRef || !draft.serviceKey) {
          el.call('openNotification', {
            title: 'Missing required fields',
            message: 'Display name, URL, project ref, and service key are required.',
            type: 'error'
          })
          return
        }
        const slug = _slugify(draft.slug || draft.displayName)
        const scope = el.call('resolveScope', draft, DEFAULT_SCOPE)
        if (scope.scopeType !== 'org' && !scope.scopeId) {
          el.call('openNotification', {
            title: 'Scope target required',
            message: `Select a ${scope.scopeType} or switch scope to Org.`,
            type: 'error'
          })
          return
        }
        s.root.update({ _supabaseSaving: true })
        const { ok, body } = await saveIntegration({
          orgId,
          kind: KIND,
          slug,
          scopeType: scope.scopeType,
          scopeId: scope.scopeId,
          displayName: draft.displayName,
          secret: draft.serviceKey,
          config: {
            display_name: draft.displayName,
            url: draft.url,
            project_ref: draft.projectRef,
            anon_key: draft.anonKey || undefined
          },
          enabled: true
        })
        s.root.update({ _supabaseSaving: false })
        if (ok) {
          s.root.update({
            _supabaseFormOpen: false,
            _newSupabaseDraft: null
          })
          await _loadInstances(s.root)
          el.call('openNotification', { title: `Connected ${draft.displayName}`, type: 'success' })
        } else {
          el.call('openNotification', {
            title: 'Connect failed',
            message: body?.error || 'Unable to save integration. Does the Symbols server proxy exist?',
            type: 'error'
          })
        }
      }
    }
  }
}

export const SupabaseFormField = {
  flow: 'y',
  gap: 'V',
  Label: {
    tag: 'label',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '700',
    text: (el, s) => s.label
  },
  Input: {
    tag: 'input',
    type: 'text',
    width: '100%',
    padding: 'Z',
    round: 'Y',
    background: 'document',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    color: 'title',
    fontSize: 'Z2',
    placeholder: (el, s) => s.placeholder || '',
    value: (el, s) => {
      const draft = s.root?._newSupabaseDraft || {}
      return draft[s.draftKey] || ''
    },
    onInput: (e, el, s) => {
      s.root.update({
        _newSupabaseDraft: {
          ...(s.root?._newSupabaseDraft || {}),
          [s.draftKey]: e.target.value
        }
      })
    }
  }
}

export const SupabaseInstanceList = {
  flow: 'y',
  gap: 'A',
  childExtends: 'SupabaseInstanceCard',
  childrenAs: 'state',
  children: (el, s) => {
    const list = s.root?._integrationHub?.integrations || []
    return list.filter((i) => i.kind === KIND)
  },
  EmptyState: {
    hide: (el, s) => {
      const list = s.root?._integrationHub?.integrations || []
      return list.filter((i) => i.kind === KIND).length > 0
    },
    padding: 'B',
    round: 'Z1',
    background: 'overlay',
    borderWidth: '1px',
    borderStyle: 'dashed',
    borderColor: 'line',
    textAlign: 'center',
    color: 'caption',
    fontSize: 'Z2',
    text: 'No Supabase projects connected yet. Click "Connect another project" to add the first one.'
  }
}

export const SupabaseInstanceCard = {
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  padding: 'A',
  flow: 'y',
  gap: 'Z1',

  TopRow: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',

    Left: {
      flow: 'x',
      align: 'center',
      gap: 'Z1',
      Icon: { icon: 'database', boxSize: 'A' },
      Info: {
        flow: 'y',
        gap: 'V',
        Name: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          text: (el, s) => s.display_name || s.slug
        },
        Meta: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          text: (el, s) => `${s.config?.project_ref || '—'} · slug: ${s.slug}`
        }
      }
    },

    Right: {
      flow: 'x',
      gap: 'Z',
      align: 'center',

      ScopeBadge: { extends: 'IntegrationScopeBadge' },

      StatusPill: {
        tag: 'span',
        padding: 'V Z',
        round: 'Y',
        fontSize: 'Y1',
        fontWeight: '700',
        background: (el, s) => (s.enabled && s.has_secret ? 'green' : 'overlayStrong'),
        color: (el, s) => (s.enabled && s.has_secret ? 'white' : 'caption'),
        text: (el, s) => {
          if (!s.has_secret) return 'Missing secret'
          if (!s.enabled) return 'Disabled'
          return 'Live'
        }
      },

      BrowseBtn: {
        tag: 'button',
        type: 'button',
        padding: 'V Z',
        round: 'Y',
        background: 'blue',
        color: 'white',
        fontSize: 'Y2',
        fontWeight: '700',
        cursor: 'pointer',
        border: 'none',
        text: 'Browse data',
        onClick: (e, el, s) => {
          const root = el.call('findRouteRoot', el)
          if (root) el.router(`/data/external?slug=${encodeURIComponent(s.slug)}`, root)
        }
      },

      RemoveBtn: {
        tag: 'button',
        type: 'button',
        padding: 'V Z',
        round: 'Y',
        background: 'transparent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        color: 'red',
        fontSize: 'Y2',
        cursor: 'pointer',
        text: 'Remove',
        onClick: async (e, el, s) => {
          if (!window.confirm(`Disconnect ${s.display_name || s.slug}? The external project is not modified.`)) return
          const orgId = s.root?.jwtClaims?.workspace_org_id
          if (!orgId) return
          const { ok, body } = await deleteIntegration({
            orgId, kind: KIND, slug: s.slug,
            scopeType: s.scope_type || 'org',
            scopeId: s.scope_id || null
          })
          if (ok) {
            await _loadInstances(s.root)
            el.call('openNotification', { title: 'Disconnected', type: 'success' })
          } else {
            el.call('openNotification', {
              title: 'Remove failed',
              message: body?.error || 'Server error',
              type: 'error'
            })
          }
        }
      }
    }
  },

  ConfigRow: {
    flow: 'x',
    gap: 'B',
    fontSize: 'Y2',
    color: 'caption',
    flexWrap: 'wrap',
    Url: { text: (el, s) => s.config?.url ? `URL: ${s.config.url}` : '' },
    HasAnon: { text: (el, s) => (s.config?.anon_key ? '· Anon: set' : '· Anon: not set (no realtime)') }
  }
}
